import React, { useState, useEffect } from 'react';
import camelCase from 'lodash/camelCase';

// Utils
import {
  editorStateToJsonString,
  isEmpty,
  createRichTextEditorState,
} from 'Wpds/components/rich-text/utils/EditorState.utils';
import { generateHeaderForQuestion } from 'Shared/utils/Reviews.utils';

// Helpers
import * as ReviewFormTemplatesHelpers from 'Shared/model_helpers/ReviewFormTemplates.helper';

// Components
import { WpContainer, WpContainerHeader, WpContainerBody, WpContainerFooter } from 'Wpds/components/container';
import { WpListSelect } from 'Wpds/components/list';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpRichTextInput } from 'Wpds/components/rich-text';
import { WpCollapsible } from 'Wpds/components/collapsible';
import ReviewRatingChart from '../ReviewRatingChart/ReviewRatingChart';
import PrivateQuestionPopover from '../ReviewQuestion/PrivateQuestionPopover';

//ReviewRatingChart

// Styles
import './RatingQuestion.scss';

type Props = {
  question;
  isOpen: boolean;
  handleMoveNext?: () => void;
  handleMoveBack?: () => void;
  answers;
  formData?;
  handleSetAnswers: Function;
  isSummaryQuestion?: boolean;
  isPreview?: boolean;
  isSelfReview?: boolean;
  receiverName: string;
};

const RatingQuestion = ({
  receiverName,
  question,
  isOpen,
  handleMoveNext,
  handleMoveBack,
  answers,
  formData,
  handleSetAnswers,
  isPreview = true,
  isSummaryQuestion,
  isSelfReview = false,
}: Props) => {
  const questionKey = ReviewFormTemplatesHelpers.getQuestionKey(question);

  const [editorState, setEditorState] = useState(answers[questionKey]?.explanation || createRichTextEditorState(''));

  const [rating, setRating] = useState(
    answers[questionKey]?.rating ? [{ value: answers[questionKey]?.rating, label: answers[questionKey]?.rating }] : []
  );
  const header = generateHeaderForQuestion({ question });
  const defaultDescription = ReviewFormTemplatesHelpers.getQuestionDescription(question);
  const selfReviewDescription = ReviewFormTemplatesHelpers.getQuestionSelfReviewDescription(question);
  const description = isSelfReview && selfReviewDescription ? selfReviewDescription : defaultDescription;
  const explanationDescription = ReviewFormTemplatesHelpers.getQuestionExplanationDescription(question);
  const showExplanation =
    ReviewFormTemplatesHelpers.getQuestionShowExplanation(question) !== false || isSummaryQuestion;
  const values = ReviewFormTemplatesHelpers.getValues(question).toJS();
  const isNextDisabled = isPreview ? false : (isEmpty(editorState) && showExplanation) || !rating || !rating.length;
  const placeholder = '';
  const showBadge = ReviewFormTemplatesHelpers.getQuestionPrivate(question);
  const showOptional = ReviewFormTemplatesHelpers.getQuestionOptional(question);

  const options = values.map(value => {
    return {
      value: value.number.toString(),
      label: value.number.toString(),
      tooltip: value.label,
    };
  });
  const handleSaveAnswers = () => {
    const data = {
      [questionKey]: {
        explanation: editorStateToJsonString(editorState),
        rating: rating[0]?.value || null,
      },
    };
    handleSetAnswers(data);
  };

  const HorizontalBarChartForSummary = () => {
    const groupAverages = formData?.toJS()?.[camelCase(questionKey)]?.groupAverages || {};
    const groups = Object.keys(groupAverages);
    const groupToLabel = {
      receiver: 'Self Score: ',
      peers: 'Avg. Peer Score: ',
      compilerPeerReview: 'Manager (As a peer feedback provider) Score: ',
    };
    const bars = groups
      .filter(group => groupToLabel[group])
      .map(group => ({ ...groupAverages[group], label: groupToLabel[group] }));
    return <ReviewRatingChart groups={bars} options={options} />;
  };

  useEffect(() => {
    if (editorState || !showExplanation) handleSaveAnswers();
  }, [editorState, rating]);

  const baseClassName = 'RatingQuestion';
  return (
    <WpContainer className={baseClassName}>
      <WpContainerHeader>
        <div className={`${baseClassName}__header-container`}>
          <span>
            {header} {showOptional && <span>(Optional)</span>}
          </span>
          {showBadge && <PrivateQuestionPopover receiverName={receiverName}></PrivateQuestionPopover>}
        </div>
      </WpContainerHeader>

      <WpCollapsible open={isOpen}>
        <>
          <WpContainerBody>
            {!isSummaryQuestion &&
              description &&
              description?.split('\n') &&
              description.split('\n').map((description_line, index) => (
                <p className={`${baseClassName}__question`} key={`${questionKey}-dl-${index}`}>
                  {description_line}
                </p>
              ))}
            {isSummaryQuestion && <HorizontalBarChartForSummary />}
            {isSummaryQuestion && (
              <div className={`${baseClassName}__question mb-2 mt-4`}>
                Please provide the final rating that will appear in their review
              </div>
            )}
            <WpListSelect
              id={questionKey}
              options={options}
              direction="horizontal"
              max={1}
              showIcon={false}
              selectedOptions={rating || []}
              disableOnMax={false}
              // @ts-ignore
              onChange={setRating}
            />
            {!isSummaryQuestion && showExplanation && explanationDescription && (
              <p className={`${baseClassName}__question`}>{explanationDescription}</p>
            )}
            {isSummaryQuestion && (
              <p className={`${baseClassName}__question`}>Summarize the reviews and why you gave that rating</p>
            )}
            {showExplanation && (
              <WpRichTextInput
                value={editorState}
                placeholder={placeholder}
                onChange={setEditorState}
                autoFocus={false}
              />
            )}
          </WpContainerBody>
          {(handleMoveBack || handleMoveNext) && (
            <WpContainerFooter justifyContent="flexEnd">
              <WpButtonGroup>
                {handleMoveBack && (
                  <WpButton onClick={handleMoveBack} variant="tertiary">
                    Back
                  </WpButton>
                )}
                {handleMoveNext && (
                  <WpButton onClick={handleMoveNext} disabled={isNextDisabled}>
                    Next
                  </WpButton>
                )}
              </WpButtonGroup>
            </WpContainerFooter>
          )}
        </>
      </WpCollapsible>
    </WpContainer>
  );
};

export default RatingQuestion;
